import { useEffect, useRef } from "react";
import { useAuthStore } from "../store/authStore";
import { restoreSession } from "../utils/restoreSession";
import { useUserStore } from "../store/user";
import { postReferralApply } from "../api/referral/apply";
import { useTelegramInitDataAuth } from "./useTelegramInitDataAuth";
import { useSearchParams } from "react-router";
import { useApplyRefcodeStore } from "../store/applyRefcode";
import { useChainId } from "wagmi";

export const useAutoAuth = () => {
  const { accessToken } = useAuthStore();
  const { setUser, setReferral, refcode, setIsAuthLoading } = useUserStore();
  const { isMiniApp } = useTelegramInitDataAuth();
  const [searchParams] = useSearchParams();

  const { isReferralError, setIsReferralError } = useApplyRefcodeStore();

  const chainId = useChainId();

  const isReady = typeof isMiniApp === "boolean" && !!chainId;

  const startedRef = useRef(false);

  useEffect(() => {
    if (!isReady) return;

    if (startedRef.current) return;
    startedRef.current = true;

    let interval: NodeJS.Timeout;

    const action = async () => {
      setIsAuthLoading(true);
      const restored = await restoreSession();

      let payload: { source: "miniapp" | "web"; referralCode?: string };

      if (isMiniApp) {
        payload = { source: "miniapp" };
      } else {
        const refcodeParam = searchParams.get("refcode") || "";
        payload = { source: "web", referralCode: refcodeParam };
      }

      if (restored || isReferralError) {
        const applyAndMaybeSet = async () => {
          const referral = await postReferralApply(payload);
          if (referral) {
            setReferral(referral);
            if (referral.userReferralCode) {
              setIsReferralError(false);
              clearInterval(interval);
            }
          }
        };

        applyAndMaybeSet();

        interval = setInterval(() => {
          if (!refcode) {
            applyAndMaybeSet();
          }
        }, 20000);
      }

      await setUser(chainId || 0);

      setIsAuthLoading(false);
    };

    if (!accessToken) {
      action();
    } else if (accessToken && !isMiniApp) {
      action();
    }

    return () => {
      clearInterval(interval);
    };
  }, [
    isReady,
    accessToken,
    isMiniApp,
    refcode,
    searchParams,
    isReferralError,
    chainId,
    setReferral,
    setUser,
    setIsAuthLoading,
    setIsReferralError,
  ]);

  return null;
};
